import { ServicePage } from './servicePages';

const SITE_URL = 'https://orbitrasolution.tech';
const SITE_NAME = 'Orbitra Solution';
const DEFAULT_DESCRIPTION = 'Orbitra Solution crafts bespoke websites, AI automation systems, and striking brand identities for ambitious businesses worldwide. Based in Surat, Gujarat.';
const DEFAULT_TITLE = 'Orbitra Solution - Digital Studio & AI Automation | Surat, India';
const OG_IMAGE = `${SITE_URL}/og-image.png`;

type MetaInput = {
  title?: string;
  description?: string;
  path?: string;
  keywords?: string[];
  service?: ServicePage;
  noindex?: boolean;
};

function upsertMeta(selector: string, create: () => HTMLMetaElement, value: string): void {
  let element = document.head.querySelector<HTMLMetaElement>(selector);
  if (!element) {
    element = create();
    document.head.appendChild(element);
  }
  element.content = value;
}

function setNameMeta(name: string, content: string): void {
  upsertMeta(`meta[name="${name}"]`, () => {
    const meta = document.createElement('meta');
    meta.name = name;
    return meta;
  }, content);
}

function setPropertyMeta(property: string, content: string): void {
  upsertMeta(`meta[property="${property}"]`, () => {
    const meta = document.createElement('meta');
    meta.setAttribute('property', property);
    return meta;
  }, content);
}

function upsertLink(rel: string, href: string, hreflang?: string): void {
  const selector = hreflang ? `link[rel="${rel}"][hreflang="${hreflang}"]` : `link[rel="${rel}"]:not([hreflang])`;
  let link = document.head.querySelector<HTMLLinkElement>(selector);
  if (!link) {
    link = document.createElement('link');
    link.rel = rel;
    if (hreflang) link.hreflang = hreflang;
    document.head.appendChild(link);
  }
  link.href = href;
}

function setJsonLd(id: string, data: unknown): void {
  let script = document.getElementById(id) as HTMLScriptElement | null;
  if (!script) {
    script = document.createElement('script');
    script.id = id;
    script.type = 'application/ld+json';
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(data);
}

export function applySeoMeta(input: MetaInput = {}): void {
  const path = input.path ?? window.location.pathname;
  const url = `${SITE_URL}${path === '/' ? '/' : path}`;
  const title = input.title ?? DEFAULT_TITLE;
  const description = input.description ?? DEFAULT_DESCRIPTION;
  const robots = input.noindex ? 'noindex, follow' : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';

  document.title = title;
  setNameMeta('title', title);
  setNameMeta('description', description);
  setNameMeta('robots', robots);
  setNameMeta('googlebot', robots);
  setNameMeta('bingbot', robots);
  setNameMeta('keywords', input.keywords?.join(', ') ?? 'digital studio, website design, AI automation, brand identity, Orbitra Solution');

  upsertLink('canonical', url);
  upsertLink('alternate', url, 'en');
  upsertLink('alternate', url, 'en-US');
  upsertLink('alternate', url, 'x-default');

  setPropertyMeta('og:url', url);
  setPropertyMeta('og:title', title);
  setPropertyMeta('og:description', description);
  setPropertyMeta('og:image', OG_IMAGE);
  setPropertyMeta('og:image:secure_url', OG_IMAGE);
  setPropertyMeta('og:image:type', 'image/png');
  setPropertyMeta('og:image:width', '1200');
  setPropertyMeta('og:image:height', '630');
  setPropertyMeta('og:image:alt', `${SITE_NAME} - Digital Studio & AI Automation`);

  setNameMeta('twitter:url', url);
  setNameMeta('twitter:title', title);
  setNameMeta('twitter:description', description);
  setNameMeta('twitter:image', OG_IMAGE);
  setNameMeta('twitter:image:alt', `${SITE_NAME} - Digital Studio & AI Automation`);

  if (input.service) {
    setServiceJsonLd(input.service, url);
  } else {
    const oldServiceSchema = document.getElementById('service-page-jsonld');
    oldServiceSchema?.remove();
  }
}

function setServiceJsonLd(service: ServicePage, url: string): void {
  setJsonLd('service-page-jsonld', {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Service',
        '@id': `${url}#service`,
        name: service.title,
        serviceType: service.title,
        url,
        description: service.metaDescription,
        keywords: service.keywords,
        provider: { '@id': `${SITE_URL}/#organization` },
        areaServed: 'Worldwide',
        offers: {
          '@type': 'Offer',
          url,
          availability: 'https://schema.org/InStock',
          priceCurrency: 'INR',
          seller: { '@id': `${SITE_URL}/#organization` },
        },
      },
      {
        '@type': 'WebPage',
        '@id': `${url}#webpage`,
        url,
        name: service.metaTitle,
        description: service.metaDescription,
        isPartOf: { '@id': `${SITE_URL}/#website` },
        about: { '@id': `${url}#service` },
        inLanguage: 'en-US',
        speakable: {
          '@type': 'SpeakableSpecification',
          cssSelector: ['h1', 'h2', '.speakable'],
        },
        primaryImageOfPage: {
          '@type': 'ImageObject',
          url: OG_IMAGE,
          width: 1200,
          height: 630,
          caption: `${SITE_NAME} ${service.title}`,
        },
      },
      {
        '@type': 'FAQPage',
        '@id': `${url}#faq`,
        mainEntity: service.faqs.map((faq) => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: faq.answer,
          },
        })),
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${url}#breadcrumb`,
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
          { '@type': 'ListItem', position: 2, name: service.title, item: url },
        ],
      },
    ],
  });
}

